import { z } from 'zod';

/**
 * Mirrors `backend/src/modules/platform/features/features.validation.ts`.
 *
 * The server is still the authority (CLAUDE.md §6); this copy exists so a typo
 * is caught as it is typed rather than after a round trip.
 *
 * No `category`: the checklist is one flat list, so there is no grouping for a
 * person adding a feature to choose. The column still exists and the server
 * fills it — see `features.service.ts`.
 */

/** The details a feature can be corrected on after it exists. */
export const catalogueEditSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Give the feature a name')
    .max(120, 'Keep the name under 120 characters'),
  description: z.string().trim().max(500, 'Keep the description under 500 characters'),
  /**
   * The list price in rupees. Blank means "not priced separately" and is sent
   * as `null`, so a price entered by mistake can be taken back off.
   */
  price: z
    .union([
      // Empty first: `z.coerce.number()` turns '' into 0.
      z.literal(''),
      z.coerce
        .number()
        .finite('Enter an amount in rupees')
        .nonnegative('A price cannot be negative')
        .max(9_999_999_999.99, 'That is larger than this field holds'),
    ])
    .transform((value) => (value === '' ? null : value)),
});

/**
 * The same, plus the key — which is only ever set once.
 *
 * Derived from the edit schema rather than written out again, so the two cannot
 * drift apart in what they allow (CLAUDE.md §3).
 */
export const catalogueEntrySchema = catalogueEditSchema.extend({
  key: z
    .string()
    .trim()
    .min(2, 'A key needs at least two characters')
    .max(64, 'Keep the key under 64 characters')
    .regex(/^[A-Z][A-Z0-9_]*$/, 'Use capitals, digits and underscores — e.g. FEE_REMINDERS'),
});

export type CatalogueEditPayload = z.infer<typeof catalogueEditSchema>;
export type CatalogueEntryPayload = z.infer<typeof catalogueEntrySchema>;
